import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, apiBlob, apiList, saveBlob } from '../../api/http';
import { useAuth } from '../../auth/AuthContext';
import {
  Notice,
  Pager,
  SchoolSelect,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';
import { CATEGORIES, type DocumentRow, type Message, type Person } from './shared';

type Owner = 'student' | 'staff';

/** Staff side of documents: pick a student or staff member, then upload, review, download and delete their files. */
export function DocumentsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [owner, setOwner] = useState<Owner>('student');
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<Person | null>(null);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<Message>(null);
  const [category, setCategory] = useState<string>('birth_certificate');
  const [title, setTitle] = useState('');
  const [visible, setVisible] = useState(false);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const forStudents = can('documents.view', schoolId);
  const forStaff = can('documents.staff_view', schoolId);
  const kind: Owner = owner === 'staff' && forStaff ? 'staff' : forStudents ? 'student' : 'staff';
  const canUpload = can(kind === 'staff' ? 'documents.staff_manage' : 'documents.upload', schoolId);
  const canReview = kind === 'student' && can('documents.verify', schoolId);
  const canDelete = can(kind === 'staff' ? 'documents.staff_manage' : 'documents.delete', schoolId);

  const matches = useQuery({
    queryKey: ['documents', schoolId, kind, 'people', search],
    queryFn: () =>
      apiList<Person>(kind === 'staff' ? '/staff' : '/students', {
        query: { schoolId, q: search, pageSize: 6 },
      }).then((r) => r.data),
    enabled: Boolean(schoolId) && search.trim().length >= 2 && !person,
  });
  const key = ['documents', schoolId, kind, person?.id, page];
  const docs = useQuery({
    queryKey: key,
    queryFn: () =>
      apiList<DocumentRow>(`/schools/${schoolId}/documents`, {
        query: { [kind === 'staff' ? 'staffId' : 'studentId']: person!.id, page, pageSize: 20 },
      }),
    enabled: Boolean(schoolId && person),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['documents', schoolId] });
  const fail = (e: unknown) => setMessage({ kind: 'error', text: errorMessage(e) });

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.set(kind === 'staff' ? 'staffId' : 'studentId', person!.id);
      form.set('category', category);
      if (title.trim()) form.set('title', title.trim());
      if (kind === 'student') form.set('visibleToParents', String(visible));
      form.set('file', file);
      return api(`/schools/${schoolId}/documents`, { method: 'POST', body: form });
    },
    onSuccess: () => {
      setMessage({ kind: 'ok', text: t('doc.uploaded') });
      setTitle('');
      setVisible(false);
      if (fileInput.current) fileInput.current.value = '';
      refresh();
    },
    onError: fail,
  });
  const review = useMutation({
    mutationFn: (v: { id: string; decision: 'verified' | 'rejected'; note?: string }) =>
      api(`/schools/${schoolId}/documents/${v.id}/review`, {
        method: 'POST',
        body: { decision: v.decision, note: v.note },
      }),
    onSuccess: () => {
      setRejecting(null);
      setRejectNote('');
      setMessage(null);
      refresh();
    },
    onError: fail,
  });
  const toggle = useMutation({
    mutationFn: (d: DocumentRow) =>
      api(`/schools/${schoolId}/documents/${d.id}`, {
        method: 'PATCH',
        body: { visibleToParents: !d.visibleToParents },
      }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/schools/${schoolId}/documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
    onError: fail,
  });

  const download = async (d: DocumentRow) => {
    try {
      saveBlob(await apiBlob(`/schools/${schoolId}/documents/${d.id}/file`), d.originalName);
    } catch (e) {
      fail(e);
    }
  };
  const submit = () => {
    const file = fileInput.current?.files?.[0];
    if (!file) return setMessage({ kind: 'error', text: t('doc.noFile') });
    setMessage(null);
    upload.mutate(file);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('doc.title')}</h1>
      <SchoolSelect value={schoolId} onChange={setSchoolId} />
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      {schoolId && (forStudents || forStaff) && (
        <>
          <div className="flex flex-wrap items-end gap-4">
            {forStudents && forStaff && (
              <div>
                <label htmlFor="doc-owner" className="block text-sm font-medium">
                  {t('doc.owner.student')} / {t('doc.owner.staff')}
                </label>
                <select
                  id="doc-owner"
                  className={selectClass}
                  value={kind}
                  onChange={(e) => {
                    setOwner(e.target.value as Owner);
                    setPerson(null);
                    setSearch('');
                    setPage(1);
                  }}
                >
                  <option value="student">{t('doc.owner.student')}</option>
                  <option value="staff">{t('doc.owner.staff')}</option>
                </select>
              </div>
            )}
            <div className="relative min-w-64 flex-1">
              <TextField
                label={t(kind === 'staff' ? 'doc.search.staff' : 'doc.search.student')}
                value={person ? `${person.firstName} ${person.lastName}` : search}
                onChange={(e) => {
                  setPerson(null);
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
              {!person && matches.data && matches.data.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full rounded border border-slate-200 bg-white text-sm shadow">
                  {matches.data.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left hover:bg-slate-50"
                        onClick={() => setPerson(p)}
                      >
                        {p.firstName} {p.lastName}
                        {(p.admissionNumber ?? p.employeeNumber)
                          ? ` (${p.admissionNumber ?? p.employeeNumber})`
                          : ''}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {!person && <p className="text-slate-600">{t('doc.pick')}</p>}

          {person && canUpload && (
            <form
              noValidate
              aria-label={t('doc.upload')}
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
            >
              <div>
                <label htmlFor="doc-file" className="block text-sm font-medium">
                  {t('doc.file')}
                </label>
                <input
                  id="doc-file"
                  ref={fileInput}
                  type="file"
                  accept="application/pdf,image/png,image/jpeg"
                  className="mt-1 block w-full text-sm"
                />
              </div>
              <div>
                <label htmlFor="doc-category" className="block text-sm font-medium">
                  {t('doc.category')}
                </label>
                <select
                  id="doc-category"
                  className={selectClass}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`doc.cat.${c}` as MessageKey)}
                    </option>
                  ))}
                </select>
              </div>
              <TextField
                label={t('doc.titleField')}
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
              />
              {kind === 'student' && (
                <label className="flex items-center gap-2 self-end text-sm">
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={(e) => setVisible(e.target.checked)}
                  />
                  {t('doc.visible')}
                </label>
              )}
              <div>
                <button type="submit" className={primaryButton} disabled={upload.isPending}>
                  {t('doc.add')}
                </button>
              </div>
            </form>
          )}

          {person && docs.data?.data.length === 0 && (
            <p className="text-slate-600">{t('doc.empty')}</p>
          )}
          <ul className="space-y-2">
            {docs.data?.data.map((d) => (
              <li key={d.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <strong>{d.title}</strong> · {t(`doc.cat.${d.category}` as MessageKey)} ·{' '}
                    {t('doc.size', { kb: Math.max(1, Math.round(d.sizeBytes / 1024)) })}
                    {d.visibleToParents ? ` · ${t('doc.parentsSee')}` : ''}
                  </span>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium">
                    {t(`doc.status.${d.status}` as MessageKey)}
                  </span>
                </div>
                {d.reviewNote && <p className="mt-1 text-slate-600">{d.reviewNote}</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('doc.download')} ${d.title}`}
                    onClick={() => void download(d)}
                  >
                    {t('doc.download')}
                  </button>
                  {canReview && d.status !== 'verified' && (
                    <button
                      type="button"
                      className={secondaryButton}
                      aria-label={`${t('doc.verify')} ${d.title}`}
                      onClick={() => review.mutate({ id: d.id, decision: 'verified' })}
                    >
                      {t('doc.verify')}
                    </button>
                  )}
                  {canReview && d.status !== 'rejected' && (
                    <button
                      type="button"
                      className={secondaryButton}
                      aria-label={`${t('doc.reject')} ${d.title}`}
                      onClick={() => setRejecting(d.id)}
                    >
                      {t('doc.reject')}
                    </button>
                  )}
                  {kind === 'student' && canUpload && (
                    <button
                      type="button"
                      className={secondaryButton}
                      aria-label={`${t(d.visibleToParents ? 'doc.hideFromParents' : 'doc.showToParents')} ${d.title}`}
                      onClick={() => toggle.mutate(d)}
                    >
                      {t(d.visibleToParents ? 'doc.hideFromParents' : 'doc.showToParents')}
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className={secondaryButton}
                      aria-label={`${t('doc.delete')} ${d.title}`}
                      onClick={() => {
                        if (window.confirm(t('doc.confirmDelete'))) remove.mutate(d.id);
                      }}
                    >
                      {t('doc.delete')}
                    </button>
                  )}
                </div>
                {rejecting === d.id && (
                  <form
                    className="mt-2 flex flex-wrap items-end gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      review.mutate({ id: d.id, decision: 'rejected', note: rejectNote });
                    }}
                  >
                    <TextField
                      label={t('doc.rejectReason')}
                      value={rejectNote}
                      maxLength={300}
                      onChange={(e) => setRejectNote(e.target.value)}
                    />
                    <button type="submit" className={primaryButton} disabled={!rejectNote.trim()}>
                      {t('doc.confirmReject')}
                    </button>
                  </form>
                )}
              </li>
            ))}
          </ul>
          {docs.data && docs.data.meta.totalPages > 1 && (
            <Pager page={page} totalPages={docs.data.meta.totalPages} onPage={setPage} />
          )}
        </>
      )}
    </div>
  );
}
